// A Storage-like wrapper that never throws.
//
// Touching `window.sessionStorage` / `window.localStorage` raises a
// SecurityError when the browser blocks storage entirely — sandboxed iframes,
// Safari "Block All Cookies", some privacy modes. LiveView's own constructor
// reads sessionStorage during init, so an unguarded access crashes the whole
// app on page load before any hook runs (#331). Every storage touch in the app
// should go through here (or an equivalent try/catch) so a blocked store just
// degrades to in-memory rather than throwing.
//
// Pass the results to `new LiveSocket(..., { sessionStorage, localStorage })`
// and use them directly in hooks.
function memoryStorage() {
  const map = new Map()
  return {
    getItem: (k) => (map.has(k) ? map.get(k) : null),
    setItem: (k, v) => map.set(k, String(v)),
    removeItem: (k) => map.delete(k),
  }
}

// `kind` is "local" or "session". Returns the real Storage if it's reachable
// and writable, otherwise a process-lifetime in-memory fallback.
export function safeStorage(kind) {
  const fallback = memoryStorage()
  let store
  try {
    store = kind === "session" ? window.sessionStorage : window.localStorage
    // Access alone can throw; a probe write catches read-only stores too.
    const probe = "__armchair_probe__"
    store.setItem(probe, "1")
    store.removeItem(probe)
  } catch (_) {
    return fallback
  }

  // Wrap each method so a later quota/security error still can't throw.
  return {
    getItem: (k) => {
      try {
        return store.getItem(k)
      } catch (_) {
        return fallback.getItem(k)
      }
    },
    setItem: (k, v) => {
      try {
        store.setItem(k, v)
      } catch (_) {
        fallback.setItem(k, v)
      }
    },
    removeItem: (k) => {
      try {
        store.removeItem(k)
      } catch (_) {
        fallback.removeItem(k)
      }
    },
  }
}
